import { useState, useEffect, useRef } from 'react';
import { Modal } from '@mui/material';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { toggleModal } from '../../reducers/modalReducer';
import socket, { postRequest, showMessage } from '../../config';
import { SHOW_AI_FEATURES } from '../../env';
import './autoFill.css';


const VERDICT_STYLES = {
    correct:   { bg: '#ECFDF5', border: '#A7F3D0', color: '#059669', label: 'Correct' },
    incorrect: { bg: '#FFF5F7', border: '#FCA5A5', color: '#DC2626', label: 'Incorrect' },
};

const btnCancel = { background: 'none', border: '1.5px solid #D1D5DB', borderRadius: '50px', cursor: 'pointer', color: '#6B7280', fontSize: '13px', fontWeight: 500, padding: '0.4rem 1.5rem' };
const btnMinimize = { background: 'none', border: '1.5px solid #FC7785', borderRadius: '50px', cursor: 'pointer', color: '#FC7785', fontSize: '13px', fontWeight: 500, padding: '0.4rem 1.5rem' };

const extractSuggestedQNo = (reason) => {
    if (!reason) return null;
    const match = reason.match(/under\s+([A-Z0-9]+(?:\.[0-9]+(?:\.[0-9]+)*)?)\s*:/i) || reason.match(/belongs to\s+([A-Z0-9]+(?:\.[0-9]+(?:\.[0-9]+)*)?)\s*:/i);
    return match ? match[1] : null;
};

const DiagnoseReportModal = () => {
    const dispatch = useDispatch();
    const modal = useSelector(state => state.modal);
    const isOpen = modal.open === 'diagnoseReport';
    const loading = !!modal.diagnoseLoading;
    const results = modal.diagnoseResults || [];
    const summary = modal.diagnoseSummary || null;
    const moduleName = modal.diagnoseModuleName || '';

    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [fixedCards, setFixedCards] = useState(new Set());
    const [fixingCards, setFixingCards] = useState(new Set());
    const [applyingAll, setApplyingAll] = useState(false);
    const [rolledBackCards, setRolledBackCards] = useState(new Set());
    const [rollingBackCards, setRollingBackCards] = useState(new Set());
    // Live progress while diagnose runs — mirrors autofill's "Currently working on" card via matching diagnose:* socket events.
    // Each batch fires ~20 diagnose:reading events almost simultaneously (batch verification runs in parallel, not
    // sequentially) — queuing + draining them at a fixed cadence makes the card visibly rotate through real questions
    // instead of freezing on whichever one happened to arrive last, mirroring the autofill indexing queue pattern.
    const [diagnoseProgress, setDiagnoseProgress] = useState({ total: 0, current: 0, correct: 0, incorrect: 0 });
    const [diagnoseReading, setDiagnoseReading] = useState(null); // { qNo, questionText, files }
    const readingQueueRef = useRef([]);
    const readingAnimatingRef = useRef(false);
    const readingTimerRef = useRef(null);

    useEffect(() => { if (!isOpen) { setFilter('all'); setSearch(''); setFixedCards(new Set()); setFixingCards(new Set()); setApplyingAll(false); } }, [isOpen]);

    useEffect(() => {
        if (loading) {
            setDiagnoseProgress({ total: 0, current: 0, correct: 0, incorrect: 0 });
            setDiagnoseReading(null);
            readingQueueRef.current = [];
            readingAnimatingRef.current = false;
            clearTimeout(readingTimerRef.current);
        }
    }, [loading]);

    useEffect(() => {
        const companyId = modal.diagnoseCompanyId;
        const drainReadingQueue = () => {
            if (readingQueueRef.current.length === 0) { readingAnimatingRef.current = false; return; }
            const next = readingQueueRef.current.shift();
            setDiagnoseReading(next);
            readingTimerRef.current = setTimeout(drainReadingQueue, 1400);
        };
        const onStarted = (data) => { if (data.companyId === companyId) setDiagnoseProgress({ total: data.total || 0, current: 0, correct: 0, incorrect: 0 }); };
        const onReading = (data) => {
            if (data.companyId !== companyId) return;
            readingQueueRef.current.push({ qNo: data.qNo, questionText: data.questionText, files: data.files || [] });
            if (!readingAnimatingRef.current) { readingAnimatingRef.current = true; drainReadingQueue(); }
        };
        const onProgress = (data) => { if (data.companyId === companyId) setDiagnoseProgress({ total: data.total || 0, current: data.current || 0, correct: data.correct || 0, incorrect: data.incorrect || 0 }); };
        socket.on('diagnose:started', onStarted);
        socket.on('diagnose:reading', onReading);
        socket.on('diagnose:progress', onProgress);
        return () => {
            clearTimeout(readingTimerRef.current);
            socket.off('diagnose:started', onStarted);
            socket.off('diagnose:reading', onReading);
            socket.off('diagnose:progress', onProgress);
        };
    }, [modal.diagnoseCompanyId]);

    // Minimize hides the modal but leaves the in-flight /clarence/diagnose request (owned by DiagnoseButton) running —
    // DiagnoseStatusBar picks up live progress independently via its own diagnose:* listeners, same pattern as autofill
    const handleMinimize = () => dispatch(toggleModal({ open: '', diagnoseStatusBarVisible: true }));

    const handleCancel = async () => {
        dispatch(toggleModal({ diagnoseCancelled: true }));
        postRequest('/clarence/diagnose/cancel', {}).catch(() => { });
        dispatch(toggleModal({ open: '', diagnoseLoading: false, diagnoseStatusBarVisible: false }));
        showMessage('Auditor cancelled');
    };

    const handleRollbackAnswer = async (r) => {
        if (!r.answerId) return;
        setRollingBackCards(prev => new Set(prev).add(r.qNo));
        try {
            const response = await postRequest('/clarence/rollback-run', { answerId: r.answerId });
            if (response?.code === 200) {
                setRolledBackCards(prev => new Set(prev).add(r.qNo));
                // Reason: DiagnoseButton/DiagnoseReportModal are mounted outside the per-question render tree that
                // owns patchAnswerInModule, so there's no prop path to patch localModule directly — reuse the same
                // Redux flag handleApplyFix already dispatches, which QuestionAnswersPage watches to refetch the module
                dispatch(toggleModal({ diagnoseFixJustApplied: true }));
                showMessage('Answer rolled back successfully', 'success');
            } else showMessage(response?.message || 'Failed to roll back answer', 'error');
        } catch (_) {
            showMessage('Failed to roll back answer', 'error');
        }
        setRollingBackCards(prev => { const s = new Set(prev); s.delete(r.qNo); return s; });
    };

    const handleApplyFix = async (r, suggestedQNo) => {
        setFixingCards(prev => new Set(prev).add(r.qNo));
        try {
            const response = await postRequest('/clarence/apply-fix', { companyId: modal.diagnoseCompanyId, originalQNo: r.qNo, suggestedQNo, moduleType: modal.diagnoseModuleType, ...(modal.diagnoseModuleId ? { moduleId: modal.diagnoseModuleId } : {}), reason: r.reason || '', });
            if (response?.code === 200) {
                setFixedCards(prev => new Set(prev).add(r.qNo));
                dispatch(toggleModal({ diagnoseFixJustApplied: true }));
            } else showMessage(response?.message || 'Could not apply fix. Please try again.');
        } catch (_) {
            showMessage('Could not apply fix. Please try again.');
        }
        setFixingCards(prev => { const s = new Set(prev); s.delete(r.qNo); return s; });
    };

    const handleApplyAllFixes = async () => {
        const pending = results.filter(r => r.suggestedQNo && !fixedCards.has(r.qNo) && (r.files || []).length > 0);
        if (!pending.length) return;
        setApplyingAll(true);
        await Promise.all(pending.map(r => handleApplyFix(r, r.suggestedQNo)));
        setApplyingAll(false);
    };

    const filtered = results.filter(r => {
        const matchFilter = filter === 'all' || r.verdict === filter;
        const matchSearch = !search || r.question?.toLowerCase().includes(search.toLowerCase()) || r.qNo?.toLowerCase().includes(search.toLowerCase());
        return matchFilter && matchSearch;
    });

    const close = () => dispatch(toggleModal({ open: '' }));

    return (
        <Modal open={isOpen} onClose={close} className="backdrop td500 zi4 pf t0 l0 w100vw h100vh df jcc aic">
            <div className="pr shadow df fdc bg1 br10px" style={{ width: '95vw', maxWidth: '1450px', maxHeight: '90vh', overflow: 'hidden' }}>

                {/* Header */}
                <div className="df aic jcsb px2rem py1rem" style={{ borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
                    <div className="df aic" style={{ gap: '0.75rem' }}>
                        <div className="df aic jcc br10px" style={{ width: 40, height: 40, background: '#EEF2FF' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#4A67FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div>
                            <p className="c15 fw600 fs1-125rem mb0rem">Auditor Report</p>
                            {moduleName && <p className="c12 fs13px fw400 mb0rem">{moduleName}</p>}
                        </div>
                    </div>
                    {!loading && (
                        <button onClick={close} className="cp bg5-hover br100 df aic jcc" style={{ width: 32, height: 32, border: 'none', background: 'none' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" /></svg>
                        </button>
                    )}
                </div>

                {/* Loading state — live progress via diagnose:* socket events, styled like the auto-fill stepper's "Currently working on" card */}
                {loading && (() => {
                    const { total, current, correct, incorrect } = diagnoseProgress;
                    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
                    const filesLabel = diagnoseReading?.files?.length ? diagnoseReading.files.slice(0, 2).join(', ') + (diagnoseReading.files.length > 2 ? ` +${diagnoseReading.files.length - 2} more` : '') : null;
                    return (
                        <div className="df fdc" style={{ flex: 1, padding: '2.5rem 3rem', overflow: 'hidden' }}>
                            {/* Card: wizard GIF, title/subtitle, progress bar — mirrors the auto-fill stepper's "Filling Answers" card */}
                            <div className="w100 df fdc px2rem py1-5rem br10px bg1 mb1rem bw1px bss bo16">
                                <div className="df aic mb1rem">
                                    <img className='w80px' src={require('../../../images/wizard.gif')} alt="processing" />
                                    <div className="df fdc" style={{ flex: 1 }}>
                                        <p className="w100 c15 fw600 fs15px mb0-25rem">Clarence is verifying answers{total > 0 && ` — ${total} answers`}</p>
                                        <p className="c27 fs12px fw300 fsi">Checking each answer against your uploaded documents — this may take a few minutes.</p>
                                    </div>
                                    {(correct > 0 || incorrect > 0) && (
                                        <div className="df aic" style={{ gap: '0.5rem', flexShrink: 0 }}>
                                            <span className="fs11px fw600 br50px px0-75rem py0-25rem" style={{ background: '#ECFDF5', color: '#059669', border: '1px solid #A7F3D0' }}>{correct} correct</span>
                                            <span className="fs11px fw600 br50px px0-75rem py0-25rem" style={{ background: '#FFF5F7', color: '#DC2626', border: '1px solid #FCA5A5' }}>{incorrect} need review</span>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className='bg24 br10px h10px mb0-5rem oh'>
                                        <div className='h100 br10px' style={{ background: 'linear-gradient(90deg, #4A67FF, #818CF8)', width: `${pct}%`, transition: 'width 0.4s ease' }} />
                                    </div>
                                    <div className="df jcsb aic mt0-5rem">
                                        <span className="c14 fs12px fw400">Progress</span>
                                        <span className="c3 fs12px fw600">{current}/{total} · {pct}%</span>
                                    </div>
                                </div>
                            </div>
                            {/* Currently working on — same shape as the auto-fill stepper's live activity card */}
                            <div className="w100 df fdc p1rem br10px bg5">
                                <span className="w100 fs13px fw500 c12 mb0-5rem">Currently working on</span>
                                {diagnoseReading ? (
                                    <div className='df jcsb'>
                                        <div className='df fdc' style={{ minWidth: 0 }}>
                                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                                <img src={require('../../../images/intelligentiaIcon.png')} alt="icon" style={{ width: 22, height: 22, flexShrink: 0 }} />
                                                <p className="fs13px fw400 c15">{diagnoseReading.qNo ? `Answer ${diagnoseReading.qNo}` : 'Processing answer…'}</p>
                                            </div>
                                            {diagnoseReading.questionText && <span className="db fs12px fw400 c14 ml1-5rem mt0-25rem" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>"{diagnoseReading.questionText}"</span>}
                                        </div>
                                        <span className="bg17 fw600 fs10px br50px px1rem py0-25rem c18" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{filesLabel ? `Reading: ${filesLabel}` : 'Checking documents…'}</span>
                                    </div>
                                ) : <p className="fs13px fw400 c12 mb0rem">Starting up…</p>}
                            </div>
                            {/* Cancel + Minimize */}
                            <div className="df jcfe" style={{ gap: '0.75rem', marginTop: '1.5rem' }}>
                                <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                            </div>
                        </div>
                    );
                })()}

                {/* Results */}
                {!loading && (
                    <div className="df fdc" style={{ flex: 1, overflow: 'hidden' }}>

                        {/* Summary bar */}
                        {summary && (
                            <div className="df aic px2rem py1rem" style={{ gap: '1rem', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
                                {[
                                    { label: 'Module Section{s} reviewed', value: summary.total, bg: '#F9FAFB', color: '#374151', border: '#E5E7EB' },
                                    { label: 'Module Section{s} passed', value: summary.correct, bg: '#ECFDF5', color: '#059669', border: '#A7F3D0' },
                                    { label: 'Module Section{s} require further review', value: summary.incorrect, bg: '#FFF5F7', color: '#DC2626', border: '#FCA5A5' },
                                ].map(({ label, value, bg, color, border }) => {
                                    const v = value ?? 0;
                                    const text = label.replace('{s}', v === 1 ? '' : 's');
                                    return (
                                        <div key={label} className="df fdc aic jcc br10px" style={{ background: bg, border: `1px solid ${border}`, flex: 1, padding: '1rem' }}>
                                            <span style={{ fontSize: '1.75rem', fontWeight: 700, color, lineHeight: 1.1 }}>{v}</span>
                                            <span style={{ fontSize: '11px', fontWeight: 500, color, opacity: 0.75, marginTop: '4px', textAlign: 'center' }}>{text}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Search + filter */}
                        {results.length > 0 && (
                            <div className="df aic px2rem py0-75rem" style={{ gap: '0.5rem', borderBottom: '1px solid #F3F4F6', flexShrink: 0, flexWrap: 'wrap' }}>
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search questions…" className="fs13px fw400 c15 br50px bss bw1px bo29 px1rem py0-5rem" style={{ outline: 'none', flex: '1 1 200px', minWidth: 0 }} />
                                <div className="df aic" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
                                    {[{ key: 'all', label: 'All' }, { key: 'correct', label: 'Correct' }, { key: 'incorrect', label: 'Incorrect' }].map(({ key, label }) => (
                                        <button key={key} onClick={() => setFilter(key)} className="br50px px1rem py0-375rem fs13px cp" style={{ border: '1px solid #D1D5DB', background: filter === key ? '#fff' : '#F3F4F6', color: '#151313', fontWeight: filter === key ? 700 : 400, boxShadow: filter === key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{label}</button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Results list */}
                        <div className="oya" style={{ flex: 1, padding: '1.5rem 2rem' }}>
                            {results.length === 0 && (
                                <div className="df fdc aic jcc p3rem">
                                    <p className="c12 tac fs14px mb0rem">No answers found for the selected sources.</p>
                                </div>
                            )}

                            {filtered.length === 0 && results.length > 0 && (
                                <div className="df fdc aic jcc p2rem">
                                    <p className="c12 tac fs13px mb0rem">No results match the current filter.</p>
                                </div>
                            )}

                            {filtered.map((r, idx) => {
                                const vs = VERDICT_STYLES[r.verdict] || VERDICT_STYLES.needs_review;
                                const conf = r.confidence ?? 0;
                                // Score out of 10 with the same thresholds/colors as the file-allocation AI Confidence badge, for consistency
                                const scoreVal = Math.round(conf * 10);
                                const scoreColor = scoreVal >= 8 ? '#16a34a' : scoreVal >= 6 ? '#d97706' : '#dc2626';
                                const suggestedQNo = r.suggestedQNo ?? null;
                                const isFixed = fixedCards.has(r.qNo);
                                const isFixing = fixingCards.has(r.qNo);
                                const isRolledBack = rolledBackCards.has(r.qNo);
                                const isRollingBack = rollingBackCards.has(r.qNo);
                                const showRollback = r.source === 'clarence' && r.autoFilled && r.answerId && !isRolledBack;
                                return (
                                    <div key={idx} style={{ marginBottom: '1rem', borderRadius: '10px', background: '#fff', borderLeft: `4px solid ${vs.color}`, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', padding: '1rem 1.5rem' }}>

                                        {/* Top row: qNo + question + verdict */}
                                        <div className="df aic jcsb" style={{ gap: '0.75rem', marginBottom: '0.75rem' }}>
                                            <div className="df aic" style={{ gap: '0.5rem', flex: 1, minWidth: 0 }}>
                                                {r.qNo && <span style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{r.qNo}</span>}
                                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#151313', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.question}>{r.question}</span>
                                            </div>
                                            <span style={{ fontSize: '12px', fontWeight: 600, background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`, padding: '4px 14px', borderRadius: '50px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                {vs.label}
                                            </span>
                                        </div>

                                        {/* Answer section */}
                                        <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', marginBottom: '0.75rem' }}>
                                            <div className="df aic jcsb" style={{ marginBottom: '6px' }}>
                                                <div className="df aic" style={{ gap: '0.5rem' }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Answer</span>
                                                    <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 10px', borderRadius: '50px', ...(r.source === 'clarence' ? { background: '#EEF2FF', color: '#4A67FF', border: '1px solid #C7D2FE' } : { background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }) }}>
                                                        {r.source === 'clarence' ? 'Clarence' : 'Human'}
                                                    </span>
                                                    {isRolledBack && <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 10px', borderRadius: '50px', background: '#F3F4F6', color: '#6B7280', border: '1px solid #D1D5DB' }}>Rolled back</span>}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                                    {showRollback && (
                                                        <button onClick={() => handleRollbackAnswer(r)} disabled={isRollingBack} data-tooltip="Undo this AI-filled answer and restore your previous answer, or reset to empty if none existed"
                                                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: '#fff', border: '1px solid #FCA5A5', cursor: isRollingBack ? 'default' : 'pointer', opacity: isRollingBack ? 0.5 : 1, flexShrink: 0, padding: 0 }}>
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M3 12C3 7.02944 7.02944 3 12 3C14.8273 3 17.35 4.30367 19 6.34267M21 12C21 16.9706 16.9706 21 12 21C9.1727 21 6.65 19.6963 5 17.6573" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
                                                                <path d="M19 2V7H14" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                <path d="M5 22V17H10" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: scoreColor + '18', border: '1px solid ' + scoreColor + '40', borderRadius: 12, padding: '3px 9px', flexShrink: 0 }}>
                                                        <img src={require('../../../images/intelligentiaIcon.png')} alt="icon" style={{ width: 20, height: 20 }} />
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor }}>{scoreVal}</span>
                                                    </span>
                                                </div>
                                            </div>
                                            <p style={{ fontSize: '15px', fontWeight: 600, color: '#151313', margin: 0 }}>{r.answer || '—'}</p>
                                        </div>

                                        {/* Reasoning */}
                                        {r.reason && (
                                            <div style={{ marginBottom: '0.75rem' }}>
                                                <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '4px' }}>Reasoning</span>
                                                <p style={{ fontSize: '13px', fontWeight: 400, color: '#5E5F66', lineHeight: 1.65, margin: 0 }}>{r.reason}</p>
                                            </div>
                                        )}

                                        {/* Files + Move button row */}
                                        <div className="df aic jcsb" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
                                            {(r.files || []).length > 0 ? (
                                                <div className="df aic" style={{ gap: '0.4rem', flexWrap: 'wrap', flex: 1 }}>
                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '2px' }}>Sources</span>
                                                    {r.files.map((f, fi) => {
                                                        const url = r.fileUrls?.[fi];
                                                        const chipStyle = { fontSize: '10px', fontWeight: 500, background: '#EFF6FF', color: '#2563EB', padding: '2px 7px', borderRadius: '5px', border: '1px solid #BFDBFE', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', ...(url ? { cursor: 'pointer' } : {}) };
                                                        const inner = (<>{url && <svg width="9" height="9" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}><path d="M4 4h5v2H6v8h8v-3h2v5H4V4zm7-1h6v6h-2V6.414l-5.293 5.293-1.414-1.414L13.586 5H11V3z" fill="currentColor" /></svg>}{f}</>);
                                                        return url ? <a key={fi} href={url} target="_blank" rel="noopener noreferrer" style={chipStyle} title={f}>{inner}</a> : <span key={fi} style={chipStyle} title={f}>{inner}</span>;
                                                    })}
                                                </div>
                                            ) : <div style={{ flex: 1 }} />}

                                            {suggestedQNo && (r.files || []).length > 0 && (
                                                isFixed  ? <span style={{ fontSize: '12px', fontWeight: 600, color: '#059669', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '50px', padding: '5px 14px', whiteSpace: 'nowrap', flexShrink: 0 }}>✓ Moved to {suggestedQNo}</span>
                                                    : <button disabled={isFixing} onClick={() => handleApplyFix(r, suggestedQNo)} style={{ fontSize: '12px', fontWeight: 600, background: '#4A67FF', color: '#fff', border: 'none', borderRadius: '50px', padding: '5px 16px', cursor: isFixing ? 'default' : 'pointer', opacity: isFixing ? 0.6 : 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {isFixing ? 'Moving…' : `Move to ${suggestedQNo}`}
                                                    </button>
                                            )}

                                            {!suggestedQNo && r.verdict === 'incorrect' && (
                                                <span style={{ fontSize: '11px', fontWeight: 500, color: '#94A3B8', fontStyle: 'italic', whiteSpace: 'nowrap', flexShrink: 0 }}>No suggested fix — question doesn't match anywhere</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="df aic jcfe px2rem py1rem" style={{ borderTop: '1px solid #F3F4F6', flexShrink: 0, gap: '0.75rem' }}>
                            {results.filter(r => r.suggestedQNo && !fixedCards.has(r.qNo) && (r.files || []).length > 0).length > 0 && (
                                <button disabled={applyingAll} onClick={handleApplyAllFixes} className="br50px px2rem py0-75rem cp fw600 fs14px c1" style={{ border: 'none', background: applyingAll ? '#6B8BFF' : '#4A67FF', opacity: applyingAll ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {applyingAll
                                        ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" /></svg>Applying…</>
                                        : <>⚡ Apply All Fixes</>
                                    }
                                </button>
                            )}
                            <button onClick={close} className="bg4 c1 br50px px3rem py0-75rem cp fw500 fs14px" style={{ border: 'none' }}>Done</button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

const DiagnoseButton = ({ companyId, moduleType, moduleId, tab, activeModule }) => {
    const dispatch = useDispatch();
    const store = useStore();
    const [open, setOpen] = useState(false);
    const [selection, setSelection] = useState(new Set(['clarence', 'human']));
    const [loading, setLoading] = useState(false);
    const panelRef = useRef(null);

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, []);

    const toggleSelection = (key) => {
        setSelection(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const handleGenerateReport = async () => {
        setOpen(false);
        setLoading(true);
        dispatch(toggleModal({ open: 'diagnoseReport', diagnoseLoading: true, diagnoseCancelled: false, diagnoseResults: [], diagnoseSummary: null, diagnoseModuleName: activeModule?.heading || tab || '', diagnoseCompanyId: companyId, diagnoseModuleType: moduleType, diagnoseModuleId: moduleId || null }));
        try {
            const response = await postRequest('/clarence/diagnose', { companyId, moduleType, ...(moduleId ? { moduleId } : {}), sources: [...selection] });
            // Reason: Cancel already reset modal/loading state locally and told the backend to stop between
            // batches — this promise still resolves once that in-flight request returns, so skip writing
            // results/reopening the modal for a run the user already dismissed
            if (store.getState().modal.diagnoseCancelled) return;
            if (response?.code === 200) {
                dispatch(toggleModal({ diagnoseLoading: false, diagnoseResults: response.body.results || [], diagnoseSummary: response.body.summary || null, diagnoseStatusBarVisible: false, diagnoseStatusBarCompleted: true }));
            } else {
                dispatch(toggleModal({ open: '', diagnoseLoading: false, diagnoseStatusBarVisible: false }));
                showMessage(response?.message || 'Auditor failed. Please try again.');
            }
        } catch (_) {
            if (store.getState().modal.diagnoseCancelled) return;
            dispatch(toggleModal({ open: '', diagnoseLoading: false, diagnoseStatusBarVisible: false }));
            showMessage('Auditor failed. Please try again.');
        }
        setLoading(false);
    };

    if (!SHOW_AI_FEATURES) return null;

    return (
        <div className="pr ml0-5rem" ref={panelRef}>
            <div className="auto-fill-icon-wrap">
                <button onClick={() => setOpen(o => !o)} disabled={loading} className="cp fs13px fw600 bg1 bg3-hover c3 c1-hover bw1px bo3 bss br50px px1rem py0-5rem df jcc aic">
                    {loading
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }}><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40" strokeDashoffset="10" strokeLinecap="round" /></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}><path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    }
                    {loading ? 'Analysing...' : 'Auditor'}
                </button>
                <span className="auto-fill-hover-chip">Get an AI-powered quality check on your submitted answers</span>
            </div>

            {open && (
                <div className="pa bg1 shadow br10px zi10" style={{ minWidth: '220px', top: '110%', right: 0 }}>
                    <label className="df aic px1rem py0-5rem bg5-hover cp" style={{ gap: '0.5rem', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
                        <span className="checkbox w20px h20px pr">
                            <input type="checkbox" className="bg1 bo4 bw1px bss cp td250 w20px h20px br5px" checked={selection.has('clarence')} onChange={() => toggleSelection('clarence')} />
                        </span>
                        <span className="fs13px fw400 c14">Clarence Answers</span>
                        <span className="fs11px c12 mla wsn">(AI-filled)</span>
                    </label>

                    <label className="df aic px1rem py0-5rem bg5-hover cp" style={{ gap: '0.5rem' }}>
                        <span className="checkbox w20px h20px pr">
                            <input type="checkbox" className="bg1 bo4 bw1px bss cp td250 w20px h20px br5px" checked={selection.has('human')} onChange={() => toggleSelection('human')} />
                        </span>
                        <span className="fs13px fw400 c14">Human Answers</span>
                    </label>

                    <div className="bss bs1px bo29 mx1rem" />

                    <button disabled={selection.size === 0} onClick={handleGenerateReport} className="w100 bg3 c1 fw600 fs13px cp py0-75rem" style={{ border: 'none', borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px', opacity: selection.size === 0 ? 0.5 : 1 }}>Generate Report</button>
                </div>
            )}
        </div>
    );
};

export { DiagnoseReportModal };
export default DiagnoseButton;
